<!--
  A bottom sheet on a phone, a centred panel on a wide screen. Built on <dialog>
  so it lands in the top layer (no clipping by a scrolled ancestor), traps focus,
  and answers Escape without any of that being reimplemented here.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';
  import Icon from './Icon.svelte';

  interface Props {
    open?: boolean;
    title: string;
    /** Small line under the title: counts, timings, provenance. */
    caption?: string;
    children: Snippet;
    /** Pinned below the scrolling body. */
    footer?: Snippet;
    onclose?: () => void;
  }

  let { open = $bindable(false), title, caption, children, footer, onclose }: Props = $props();

  let dialog = $state<HTMLDialogElement | null>(null);

  $effect(() => {
    const el = dialog;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  });

  // The page behind must not scroll while the sheet is up, or a phone drag moves
  // the catalogue instead of the list.
  $effect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  });

  function shut(): void {
    open = false;
    onclose?.();
  }
</script>

<dialog
  bind:this={dialog}
  onclose={shut}
  oncancel={(e) => {
    e.preventDefault();
    shut();
  }}
  aria-label={title}
>
  <!-- Clicking the backdrop closes; clicking the panel must not bubble into it. -->
  <div
    class="scrim"
    role="presentation"
    onclick={(e) => {
      if (e.target === e.currentTarget) shut();
    }}
  >
    <!--
      showModal() focuses the first focusable child, which put a live focus ring on
      the close button every time the sheet opened — it read as "the close button is
      selected". Focusing the panel instead keeps Escape and Tab working with nothing
      pre-selected.
    -->
    <!-- svelte-ignore a11y_autofocus -->
    <div class="panel" tabindex="-1" autofocus>
      <header>
        <div class="titles">
          <h2>{title}</h2>
          {#if caption}<p class="caption">{caption}</p>{/if}
        </div>
        <!--
          `data-dpad-chrome` — the sheet's own furniture. Reachable by a remote,
          never where the FIRST press lands. Measured on the link picker at
          1280x720 with `?tv=1`: without it the entry focus was this button, and
          because it sits at the far right of the header it is aligned to the
          pixel with every row's details chevron — so Down walked the chevron
          column all the way to the bottom and the choose buttons were
          unreachable downward. Escape and Tab are the <dialog>'s own; this
          changes only where an ARROW goes (dpad-rules.ts `entryId`).
        -->
        <button type="button" class="shut" data-dpad-chrome onclick={shut} aria-label="Close">
          <Icon name="close" size={20} />
        </button>
      </header>

      <div class="body">
        {@render children()}
      </div>

      {#if footer}
        <footer>{@render footer()}</footer>
      {/if}
    </div>
  </div>
</dialog>

<style>
  dialog {
    padding: 0;
    border: 0;
    background: none;
    max-width: 100vw;
    max-height: 100dvh;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
  /* showModal enters the top layer, which ignores the ancestor transform.
     Reapply the same geometry on the dialog, retaining native focus/Escape. */
  :global([data-watch-space]) dialog {
    position: fixed;
    margin: 0;
    inset: auto;
    left: var(--watch-left);
    top: var(--watch-top);
    width: var(--watch-width);
    height: var(--watch-height);
    max-width: none;
    max-height: none;
    container: watch-dialog / size;
    transform-origin: 0 0;
    transform: translateZ(0);
  }
  :global([data-watch-space][data-sideways='true']) dialog {
    transform: translateX(var(--watch-physical-width)) rotate(90deg);
  }
  :global([data-watch-space][data-sideways='true']) dialog .scrim {
    padding: max(12px, var(--safe-t)) max(12px, var(--safe-r)) max(12px, var(--safe-b)) max(12px, var(--safe-l));
    align-items: flex-end;
  }
  :global([data-watch-space][data-sideways='true']) dialog .panel {
    max-width: 360px;
    max-height: calc(var(--watch-height) - max(12px, var(--safe-t)) - max(12px, var(--safe-b)));
    border-radius: var(--radius-lg);
  }
  dialog::backdrop {
    background: rgb(3 5 9 / 0.72);
    backdrop-filter: blur(2px);
  }

  .scrim {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  .panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 88dvh;
    background: var(--surface-1);
    border-top: 1px solid var(--line);
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    box-shadow: 0 -18px 50px rgb(0 0 0 / 0.55);
    animation: rise 220ms var(--ease);
  }
  .panel:focus {
    outline: none;
  }

  header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--line);
  }
  /* The grab handle: a phone affordance, drawn rather than implied. */
  header::before {
    content: '';
    position: absolute;
    top: 7px;
    left: 50%;
    translate: -50% 0;
    width: 34px;
    height: 4px;
    border-radius: 99px;
    background: var(--surface-3);
  }
  .panel {
    position: relative;
    padding-top: 4px;
  }

  .titles {
    min-width: 0;
    flex: 1;
  }
  h2 {
    font-size: 1.02rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .caption {
    margin-top: 2px;
    color: var(--text-faint);
    font-size: 0.76rem;
  }

  .shut {
    display: grid;
    place-content: center;
    width: 34px;
    height: 34px;
    margin: -4px -4px 0 0;
    border-radius: 99px;
    color: var(--text-dim);
    transition:
      background-color 140ms var(--ease),
      color 140ms var(--ease);
  }
  .shut:hover {
    background: var(--surface-3);
    color: var(--text);
  }

  .body {
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 8px 0 max(12px, var(--safe-b, env(safe-area-inset-bottom)));
  }

  footer {
    padding: 12px 16px max(12px, var(--safe-b, env(safe-area-inset-bottom)));
    border-top: 1px solid var(--line);
    background: var(--surface-1);
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  }

  @keyframes rise {
    from {
      transform: translateY(14px);
      opacity: 0;
    }
  }

  @media (min-width: 720px) {
    .scrim {
      align-items: center;
      padding: 24px;
    }
    .panel {
      max-width: 720px;
      max-height: 82dvh;
      border: 1px solid var(--line);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow);
      padding-top: 0;
    }
    header::before {
      display: none;
    }
    footer {
      border-radius: 0 0 var(--radius-lg) var(--radius-lg);
    }
  }
  /* Watch dialogs use the same compact panel rhythm as the player's sheets.
     Dimensions belong to the logical watch canvas, including rotated phones. */
  :global([data-watch-space]) dialog::backdrop {
    background: rgb(3 5 9 / 0.24);
    backdrop-filter: none;
  }
  :global([data-watch-space]) dialog .scrim {
    align-items: flex-end;
    justify-content: flex-end;
    padding: max(12px, var(--safe-t)) max(12px, var(--safe-r)) max(12px, var(--safe-b)) max(12px, var(--safe-l));
  }
  :global([data-watch-space]) dialog .panel {
    width: 360px;
    max-width: 100%;
    max-height: min(480px, calc(var(--watch-height) - max(12px, var(--safe-t)) - max(12px, var(--safe-b))));
    padding-top: 0;
    border: 0;
    border-radius: var(--radius);
    box-shadow: 0 8px 28px rgb(0 0 0 / 0.2);
    animation: none;
    color: var(--text);
    font-size: 13px;
    overflow: hidden;
  }
  :global([data-watch-space]) dialog header {
    align-items: center;
    flex: none;
    min-height: 44px;
    padding: 4px 4px 4px 12px;
    gap: 8px;
  }
  :global([data-watch-space]) dialog header::before { display: none; }
  :global([data-watch-space]) dialog h2 { font-size: 13px; }
  :global([data-watch-space]) dialog .caption { font-size: 11px; }
  :global([data-watch-space]) dialog .shut {
    width: 44px;
    height: 44px;
    flex: none;
    margin: 0;
    border-radius: var(--radius-sm);
    transition: none;
  }
  :global([data-watch-space]) dialog .body {
    min-height: 0;
    padding: 4px 0;
    scrollbar-width: thin;
    scrollbar-color: var(--line) transparent;
  }
  @media (pointer: coarse) {
    .shut { position: relative; }
    .shut::after { content: ""; position: absolute; width: max(100%, var(--hit)); height: max(100%, var(--hit)); top: 50%; left: 50%; transform: translate(-50%, -50%); }
  }
</style>
